class CollectionGrid {
  constructor(el) {
    this.el = el;
    this.grid = el.querySelector('[data-collection-grid-products]');
    this.infiniteScroll = el.querySelector('[data-collection-grid-infinite-scroll]');
    this.nextLink = el.querySelector('[data-infinite-scroll-next]');
    this.loader = el.querySelector('[data-collection-grid-loader]');
    this.sentinel = el.querySelector('[data-collection-grid-sentinel]');
    this.statusEl = el.querySelector('[data-collection-grid-status]');

    this.bindProductLinkClicks();

    if (!this.grid || !this.nextLink || !this.sentinel) return;

    this.loading = false;
    this.observer = null;

    this.observeSentinel();
  }

  bindProductLinkClicks() {
    this.el.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="/products/"]');
      if (!link || !this.el.contains(link)) return;

      try {
        sessionStorage.setItem('from-collection', window.location.href);
      } catch (_) {
        // sessionStorage unavailable
      }
    });
  }

  getNextUrl() {
    return this.nextLink.dataset.nextUrl || this.nextLink.href;
  }

  setLoadingState(isLoading) {
    this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (!this.loader) return;

    if (isLoading) {
      this.loader.hidden = false;
      this.infiniteScroll?.classList.add('is-loading');
      this.setStatus('Loading more products.');
    } else {
      this.loader.hidden = true;
      this.infiniteScroll?.classList.remove('is-loading');
      this.setStatus('');
    }
  }

  setStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
  }

  observeSentinel() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (!this.sentinel || !this.nextLink) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loading) {
            this.fetchNextPage();
          }
        });
      },
      { rootMargin: '300px' }
    );

    this.observer.observe(this.sentinel);
  }

  async fetchNextPage() {
    if (this.loading || !this.nextLink) return;

    this.loading = true;
    this.setLoadingState(true);

    const url = this.getNextUrl();

    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newItems = doc.querySelectorAll('.collection-grid__product-item');

      if (newItems.length === 0) {
        this.teardown('No more products to load.');
        return;
      }

      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);

      const newNextLink = doc.querySelector('[data-infinite-scroll-next]');

      if (newNextLink) {
        this.nextLink.href = newNextLink.href;
        if (newNextLink.dataset.nextUrl) {
          this.nextLink.dataset.nextUrl = newNextLink.dataset.nextUrl;
        }
        announceToScreenReader(`${newItems.length} more products loaded.`);
        this.observeSentinel();
      } else {
        this.teardown('All products loaded.');
        return;
      }
    } catch (_err) {
      this.setStatus('Could not load more products. Refresh the page to try again.');
      announceToScreenReader('Could not load more products. Refresh the page to try again.');
    } finally {
      this.loading = false;
      this.setLoadingState(false);
    }
  }

  teardown(message) {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.infiniteScroll) {
      this.infiniteScroll.hidden = true;
    }

    this.nextLink = null;
    this.sentinel = null;

    if (message) {
      this.setStatus(message);
      announceToScreenReader(message);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('.collection-grid');
  if (el) new CollectionGrid(el);
});
